import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Typography, Chip,
  CircularProgress, Alert, FormControlLabel, Checkbox,
} from '@mui/material';
import { useQuery, useMutation } from 'react-query';
import { format, parseISO } from 'date-fns';
import api, { deaLogsApi, treatmentSheetApi } from '../../utils/api';
import { isCalendarDayBeforeToday } from '../../utils/dateUtils';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import type {
  AdministrationSlot, ControlledSubstance, ControlledSubstanceLot,
  ControlledSubstanceWasteReason,
} from '../../types/treatmentSheet';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: string;
  orderName: string;
  orderDose?: string;
  orderRoute?: string;
  controlledSubstance: ControlledSubstance;
  slot: AdministrationSlot;
  currentUserId: string;
}

const SCHEDULE_LABEL: Record<string, string> = {
  SCHEDULE_II: 'C-II',
  SCHEDULE_III: 'C-III',
  SCHEDULE_IV: 'C-IV',
  SCHEDULE_V: 'C-V',
};

const SCHEDULE_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  SCHEDULE_II: 'error',
  SCHEDULE_III: 'warning',
  SCHEDULE_IV: 'info',
  SCHEDULE_V: 'default',
};

const WASTE_REASONS: { value: ControlledSubstanceWasteReason; label: string }[] = [
  { value: 'PATIENT_REFUSAL', label: 'Patient refusal' },
  { value: 'BROKEN_VIAL', label: 'Broken vial' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'PARTIAL_DOSE', label: 'Partial dose drawn' },
  { value: 'SPILL', label: 'Spill' },
  { value: 'OTHER', label: 'Other' },
];

const ControlledSubstanceAdminDialog: React.FC<Props> = ({
  open, onClose, onSuccess, orderId, orderName, orderDose, orderRoute,
  controlledSubstance, slot, currentUserId,
}) => {
  const clinicZone = useClinicTimezone();
  const [lotId, setLotId] = useState<string>('');
  const [quantityAdministered, setQuantityAdministered] = useState<string>('');
  const [quantityWasted, setQuantityWasted] = useState<string>('');
  const [wasteReason, setWasteReason] = useState<ControlledSubstanceWasteReason | ''>('');
  const [witnessId, setWitnessId] = useState<string>('');
  const [witnessAttests, setWitnessAttests] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const lotsQuery = useQuery(
    ['dea-lots', controlledSubstance.id],
    () => deaLogsApi.listLots(controlledSubstance.id, false),
    { enabled: open },
  );

  const witnessesQuery = useQuery(
    ['vet-witnesses'],
    () => api.get('/users/veterinarians').then((res) => res.data),
    { enabled: open && controlledSubstance.requiresWitness },
  );

  // Pre-select first non-expired lot
  useEffect(() => {
    if (lotsQuery.data && !lotId && lotsQuery.data.length > 0) {
      const validLots = lotsQuery.data.filter(
        (l) => !l.disposedAt && Number(l.quantityRemaining) > 0,
      );
      if (validLots.length > 0) setLotId(validLots[0].id);
    }
  }, [lotsQuery.data, lotId]);

  const mutation = useMutation(
    () =>
      treatmentSheetApi.administerControlledSubstance(orderId, {
        scheduledAt: slot.scheduledAt,
        lotId,
        quantityAdministered: Number(quantityAdministered),
        quantityWasted: quantityWasted ? Number(quantityWasted) : undefined,
        wasteReason: wasteReason || undefined,
        witnessId: witnessId || undefined,
        witnessAttests: controlledSubstance.requiresWitness ? witnessAttests : undefined,
        notes: notes || undefined,
      }),
    {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        setError(err?.response?.data?.message ?? 'Failed to record administration');
      },
    },
  );

  const selectedLot = lotsQuery.data?.find((l) => l.id === lotId);
  // `ControlledSubstanceLot.expirationDate` is a CALENDAR DAY (DATE-KINDS
  // §1.2, UTC-midnight anchored). `new Date(exp) < new Date()` compared it
  // against a MOMENT, so a lot expiring tomorrow read as expired from 00:00Z -
  // 8 PM Eastern TODAY - and a vet was refused a lot that was still in date.
  const isExpired = !!selectedLot && isCalendarDayBeforeToday(selectedLot.expirationDate, clinicZone);

  const canSubmit = (() => {
    if (!lotId) return false;
    if (!quantityAdministered || isNaN(Number(quantityAdministered))) return false;
    if (Number(quantityAdministered) <= 0) return false;
    if (controlledSubstance.requiresWitness) {
      if (!witnessId) return false;
      if (witnessId === currentUserId) return false;
      if (!witnessAttests) return false;
    }
    if (Number(quantityWasted) > 0 && !wasteReason) return false;
    return true;
  })();

  const fmtRemaining = (lot: ControlledSubstanceLot) =>
    `${Number(lot.quantityRemaining).toFixed(3)} ${controlledSubstance.unit} left`;

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const scheduleLabel = SCHEDULE_LABEL[controlledSubstance.deaSchedule];
  const scheduleColor = SCHEDULE_COLOR[controlledSubstance.deaSchedule];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <span>Administer {orderName}</span>
          <Chip label={scheduleLabel} color={scheduleColor} size="small" />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Scheduled {format(parseISO(slot.scheduledAt), 'h:mm a, MMM d')}
          {orderDose && ` · Ordered ${orderDose}`}
          {orderRoute && ` · ${orderRoute}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {lotsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !lotsQuery.data || lotsQuery.data.length === 0 ? (
          <Alert severity="warning">
            No active lots for {controlledSubstance.genericName}. Add a lot in DEA Logs &raquo; Inventory before administering.
          </Alert>
        ) : (
          <>
            <TextField
              select fullWidth label="Lot" value={lotId}
              onChange={(e) => setLotId(e.target.value)}
              size="small" sx={{ mb: 2 }}
            >
              {lotsQuery.data
                .filter((l) => !l.disposedAt && Number(l.quantityRemaining) > 0)
                .map((lot) => (
                  <MenuItem key={lot.id} value={lot.id}>
                    {lot.lotNumber} — exp {format(parseISO(lot.expirationDate), 'MMM yyyy')} — {fmtRemaining(lot)}
                  </MenuItem>
                ))}
            </TextField>

            {isExpired && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This lot has expired. Document the override reason in notes.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label={`Quantity given (${controlledSubstance.unit})`}
                value={quantityAdministered}
                onChange={(e) => setQuantityAdministered(e.target.value)}
                size="small" type="number"
                fullWidth required
                inputProps={{ step: 0.001, min: 0 }}
              />
              <TextField
                label={`Wasted (${controlledSubstance.unit})`}
                value={quantityWasted}
                onChange={(e) => setQuantityWasted(e.target.value)}
                size="small" type="number"
                fullWidth
                inputProps={{ step: 0.001, min: 0 }}
              />
            </Box>

            {Number(quantityWasted) > 0 && (
              <TextField
                select fullWidth label="Waste reason" value={wasteReason}
                onChange={(e) => setWasteReason(e.target.value as ControlledSubstanceWasteReason)}
                size="small" sx={{ mb: 2 }} required
              >
                {WASTE_REASONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>
            )}

            {controlledSubstance.requiresWitness && (
              <>
                <TextField
                  select fullWidth label="Witness (required)"
                  value={witnessId}
                  onChange={(e) => setWitnessId(e.target.value)}
                  size="small" sx={{ mb: 1 }} required
                  helperText={witnessId === currentUserId ? 'Witness must be a different user' : ''}
                  error={witnessId === currentUserId}
                  disabled={witnessesQuery.isLoading}
                >
                  {(witnessesQuery.data ?? []).map((u: any) => (
                    <MenuItem
                      key={u.id} value={u.id}
                      disabled={u.id === currentUserId}
                    >
                      {u.name}{u.id === currentUserId ? ' (you)' : ''}
                    </MenuItem>
                  ))}
                </TextField>

                <FormControlLabel
                  control={<Checkbox checked={witnessAttests} onChange={(e) => setWitnessAttests(e.target.checked)} />}
                  label={
                    <Typography variant="body2">
                      Witness was physically present and attests to the administration and waste
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <TextField
              label="Notes (optional)" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              size="small" fullWidth multiline minRows={2}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained" color="primary"
          disabled={!canSubmit || mutation.isLoading}
          onClick={() => mutation.mutate()}
          startIcon={mutation.isLoading ? <CircularProgress size={14} /> : null}
        >
          Sign &amp; Record
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ControlledSubstanceAdminDialog;
